import { useContext, useEffect, useMemo, useState } from "react";
import { Column, usePagination, useSortBy, useTable } from "react-table";
import { getDefaultPageSize } from "../../../../helpers/utils";
import NoData from "../../../../component/NoData";
import TablePagination from "../../../../component/TablePagination";
import objectAnalysisType from "../../../../types/ObjectBrowser/ObjectAnalysisType";
import { TimeRangeContext } from "../../../../context/TimeRangeContext";
import ObjectTuningApi from "../../../../api/object-tuning/ObjectTuningApi";
import Helper from "../../../../helpers/Helper";

interface AnalysisTypeItem {
    type: string;
    display: string;
}

const InsightsTable = (props: { objectAnalysis: objectAnalysisType[], selectedItem: any }) => {
    const timeRangeContext = useContext(TimeRangeContext)
    const [tableStatisticsTypes, setTableStatisticsTypes] = useState<AnalysisTypeItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getAnalysisType = async () => {
            if (props.selectedItem && typeof props.selectedItem === 'object' && 'instanceId' in props.selectedItem) {
                const analysisTypeData = await ObjectTuningApi.getAnalysisType(timeRangeContext, props.selectedItem.instanceId)
                setLoading(false)
                setTableStatisticsTypes(analysisTypeData)
            }
        }
        void getAnalysisType()
    }, [timeRangeContext, props.selectedItem])

    const definitionColumns: Column[] = useMemo(() => [
        {
            Header: 'Type',
            accessor: 'type',
            headerClassName: "col-width-30 text-nowrap",
            className: 'col-width-30',
            Cell: ({ value }) => {
                const formattedValue = tableStatisticsTypes.length ? tableStatisticsTypes.find((item) => item.type === value) : undefined
                return formattedValue ? formattedValue.display : value
            },
        },
        {
            Header: 'Index',
            headerClassName: "text-nowrap",
            accessor: 'indexname'
        },
        {
            Header: 'Index Schema',
            headerClassName: "text-nowrap",
            accessor: 'indexschema'
        },
    ], [tableStatisticsTypes]);


    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
                columns: definitionColumns,
                data: props.objectAnalysis,
                initialState: {
                    pageIndex: 0,
                    pageSize: getDefaultPageSize(),
                }
            },
            useSortBy, usePagination);

    return (
            <div className="row row-cols-1 object-browser">
                <div className="col">
                    <div className="card">
                        <div className="card-header">
                            <i className="fa-solid fa-lightbulb" style={{ color: "#a259ea", fontSize: 18, marginRight: 8 }} aria-hidden="true"></i>Insights
                            <span className="badge bg-info"
                                  data-tip="Total Columns count">{Helper.getFormattedBadgeCount(props.objectAnalysis.length)}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseObjectInsights" aria-expanded="false"
                               aria-controls="collapseObjectInsights"></i>
                        </div>
                        <div id="collapseObjectInsights" className="card-body collapse show" style={{ padding: 20 }}>
                            <div className="table-responsive">
                                <table {...getTableProps()} className="table">
                                    <thead>
                                    {headerGroups.map((headerGroup, index) => (
                                            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                                                {headerGroup.headers.map((column, index) => (
                                                        <th {...column.getHeaderProps(column.getSortByToggleProps())}
                                                            className={(column as any).className} key={index}>
                                                            {column.render('Header')}
                                                            {column.canSort ? (column.isSorted ? (column.isSortedDesc ?
                                                                            <i className="fal fa-sort-amount-up-alt"/> :
                                                                            <i className="fal fa-sort-amount-down-alt"/>) :
                                                                    <i className="fal fa-sort-amount-down-alt text-light"/>) : ''}
                                                        </th>
                                                ))}
                                            </tr>
                                    ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()}>
                                    {page.map((row, index) => {
                                        prepareRow(row)
                                        return (
                                                <tr {...row.getRowProps()} key={index}>
                                                    {row.cells.map((cell) => {
                                                        return (
                                                                <td {...cell.getCellProps()} className={(cell.column as any).className}
                                                                    style={{ borderLeft: (cell.column as any).color }}>
                                                                    {cell.render('Cell')}
                                                                </td>
                                                        )
                                                    })}
                                                </tr>
                                        )
                                    })}
                                    </tbody>
                                </table>
                                <NoData
                                        error={null}
                                        loading={loading}
                                        length={props.objectAnalysis.length}
                                />
                                <TablePagination
                                        length={props.objectAnalysis.length}
                                        pageSize={pageSize}
                                        setPageSize={setPageSize}
                                        pageOptions={pageOptions}
                                        pageCount={pageCount}
                                        canPreviousPage={canPreviousPage}
                                        previousPage={previousPage}
                                        canNextPage={canNextPage}
                                        nextPage={nextPage}
                                        gotoPage={gotoPage}
                                        pageIndex={pageIndex}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    );
}

export default InsightsTable;
